import { Button, IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { Download, InsertDriveFileOutlined, ImageOutlined } from '@mui/icons-material';
import { useCallback, useState, type MouseEvent } from 'react';
import { downloadChartExcel, type ChartExcelData } from '../../utils/chartExcel';

export interface ChartDownloadMenuProps {
	onDownloadPng: () => void | Promise<void>;
	excel?: ChartExcelData;
	excelFilename: string;
	variant?: 'icon' | 'button';
	size?: 'small' | 'medium';
	disabled?: boolean;
}

const hasExcelRows = (excel: ChartExcelData | undefined): excel is ChartExcelData =>
	!!excel && Array.isArray(excel.rows) && excel.rows.length > 0;

export const ChartDownloadMenu = ({
	onDownloadPng,
	excel,
	excelFilename,
	variant = 'icon',
	size = 'medium',
	disabled = false,
}: ChartDownloadMenuProps) => {
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const open = Boolean(anchorEl);
	const showExcelOption = hasExcelRows(excel);

	const handleOpen = useCallback((event: MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	}, []);

	const handleClose = useCallback(() => {
		setAnchorEl(null);
	}, []);

	const handleDownloadPng = useCallback(() => {
		handleClose();
		void onDownloadPng();
	}, [handleClose, onDownloadPng]);

	const handleDownloadExcel = useCallback(() => {
		handleClose();
		void downloadChartExcel(excel, excelFilename);
	}, [handleClose, excel, excelFilename]);

	return (
		<>
			{variant === 'button' ? (
				<Button
					variant="contained"
					size="small"
					startIcon={<Download />}
					onClick={handleOpen}
					disabled={disabled}
					aria-label="Tải xuống biểu đồ"
					aria-haspopup="menu"
					aria-expanded={open ? 'true' : undefined}
				>
					Tải xuống
				</Button>
			) : (
				<IconButton
					onClick={handleOpen}
					disabled={disabled}
					size={size}
					aria-label="Tải xuống biểu đồ"
					aria-haspopup="menu"
					aria-expanded={open ? 'true' : undefined}
				>
					<Download fontSize={size} />
				</IconButton>
			)}
			<Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
				<MenuItem onClick={handleDownloadPng} aria-label="Tải PNG">
					<ListItemIcon>
						<ImageOutlined fontSize="small" />
					</ListItemIcon>
					<ListItemText>Tải PNG</ListItemText>
				</MenuItem>
				{showExcelOption && (
					<MenuItem onClick={handleDownloadExcel} aria-label="Tải Excel">
						<ListItemIcon>
							<InsertDriveFileOutlined fontSize="small" />
						</ListItemIcon>
						<ListItemText>Tải Excel</ListItemText>
					</MenuItem>
				)}
			</Menu>
		</>
	);
};
